import { useRef, useCallback, useEffect, useState } from 'react'

// Musical note frequencies (Hz)
const NOTE = {
  C4: 261.63, D4: 293.66, E4: 329.63, F4: 349.23,
  G4: 392.00, A4: 440.00, B4: 493.88,
  C5: 523.25, D5: 587.33, E5: 659.25, G5: 783.99, A5: 880.00,
}

// Gentle Easter-themed arpeggiated background music loop
// Progression: C major → G major → A minor → F major
const MUSIC_NOTES = [
  [NOTE.C4, 0.0], [NOTE.E4, 0.4], [NOTE.G4, 0.8], [NOTE.C5, 1.2],
  [NOTE.G4, 2.0], [NOTE.B4, 2.4], [NOTE.D5, 2.8], [NOTE.G4, 3.2],
  [NOTE.A4, 4.0], [NOTE.C5, 4.4], [NOTE.E5, 4.8], [NOTE.A4, 5.2],
  [NOTE.F4, 6.0], [NOTE.A4, 6.4], [NOTE.C5, 6.8], [NOTE.F4, 7.2],
]
const LOOP_DURATION = 8.4

export function useGameAudio() {
  const ctxRef = useRef(null)
  const masterRef = useRef(null)
  const loopTimerRef = useRef(null)
  const [isMuted, setIsMuted] = useState(false)
  const [musicStarted, setMusicStarted] = useState(false)
  // Refs mirror state so callbacks always read the latest value without re-creating
  const isMutedRef = useRef(false)
  const musicStartedRef = useRef(false)

  useEffect(() => { isMutedRef.current = isMuted }, [isMuted])
  useEffect(() => { musicStartedRef.current = musicStarted }, [musicStarted])

  const getCtx = useCallback(() => {
    if (!ctxRef.current) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext
      if (!AudioCtx) return null
      ctxRef.current = new AudioCtx()
      const master = ctxRef.current.createGain()
      master.gain.value = 1
      master.connect(ctxRef.current.destination)
      masterRef.current = master
    }
    if (ctxRef.current.state === 'suspended') {
      ctxRef.current.resume().catch((_err) => {
        // Resume may fail if the context is being closed; safe to ignore
      })
    }
    return ctxRef.current
  }, [])

  // Low-level helper: schedule a single tone on the master gain bus
  const playTone = useCallback((freq, startTime, duration, volume = 0.15, type = 'sine') => {
    const ctx = ctxRef.current
    const master = masterRef.current
    if (!ctx || !master) return
    try {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = type
      osc.frequency.setValueAtTime(freq, startTime)
      gain.gain.setValueAtTime(0.001, startTime)
      gain.gain.linearRampToValueAtTime(volume, startTime + 0.015)
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration)
      osc.connect(gain)
      gain.connect(master)
      osc.start(startTime)
      osc.stop(startTime + duration + 0.02)
    } catch {
      // Silently ignore if audio graph is in a bad state
    }
  }, [])

  const playSound = useCallback((type) => {
    if (isMutedRef.current) return
    try {
      const ctx = getCtx()
      if (!ctx) return
      const t = ctx.currentTime
      switch (type) {
        case 'select':
          playTone(650, t, 0.07, 0.08)
          break
        case 'correct':
          playTone(NOTE.E4, t, 0.20, 0.18)
          playTone(NOTE.G4, t + 0.10, 0.20, 0.18)
          playTone(NOTE.C5, t + 0.20, 0.35, 0.22)
          break
        case 'wrong':
          playTone(280, t, 0.12, 0.12, 'sawtooth')
          playTone(240, t + 0.08, 0.15, 0.08, 'sine')
          break
        case 'wordPlace':
          playTone(880, t, 0.08, 0.12)
          playTone(NOTE.E5, t + 0.04, 0.12, 0.10)
          break
        case 'wordWrong':
          playTone(260, t, 0.10, 0.10, 'triangle')
          break
        case 'complete':
          // Triumphant ascending fanfare
          playTone(NOTE.C4, t + 0.00, 0.25, 0.20)
          playTone(NOTE.E4, t + 0.15, 0.25, 0.20)
          playTone(NOTE.G4, t + 0.30, 0.25, 0.20)
          playTone(NOTE.C5, t + 0.45, 0.50, 0.25)
          playTone(NOTE.E5, t + 0.50, 0.40, 0.15)
          playTone(NOTE.G5, t + 0.55, 0.35, 0.15)
          playTone(NOTE.C5, t + 0.70, 0.60, 0.20)
          break
        case 'verseComplete': {
          // Joyful ascending bell cascade
          const bellNotes = [NOTE.C4, NOTE.E4, NOTE.G4, NOTE.C5, NOTE.E5, NOTE.G5, NOTE.A5]
          bellNotes.forEach((f, i) => {
            playTone(f, t + i * 0.10, 0.40, 0.15)
          })
          break
        }
        default:
          break
      }
    } catch {
      // Silently ignore: audio may be unavailable or context closed
    }
  }, [getCtx, playTone])

  // Schedule one full loop of background music starting at `startAt` (AudioContext time)
  const scheduleLoop = useCallback((startAt) => {
    if (!ctxRef.current || !masterRef.current) return
    const ctx = ctxRef.current
    MUSIC_NOTES.forEach(([freq, offset]) => {
      const s = startAt + offset
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.setValueAtTime(freq, s)
      gain.gain.setValueAtTime(0.001, s)
      gain.gain.linearRampToValueAtTime(0.045, s + 0.025)
      gain.gain.exponentialRampToValueAtTime(0.001, s + 0.32)
      osc.connect(gain)
      gain.connect(masterRef.current)
      osc.start(s)
      osc.stop(s + 0.34)
    })
    // Pre-schedule the next loop before this one ends for seamless looping
    const nextLoopStart = startAt + LOOP_DURATION
    const msUntilNext = Math.max(100, (nextLoopStart - ctx.currentTime - 0.8) * 1000)
    loopTimerRef.current = setTimeout(() => {
      if (musicStartedRef.current && ctxRef.current) {
        scheduleLoop(nextLoopStart)
      }
    }, msUntilNext)
  }, [])

  const startMusic = useCallback(() => {
    if (musicStartedRef.current) return
    try {
      const ctx = getCtx()
      if (!ctx) return
      setMusicStarted(true)
      musicStartedRef.current = true
      scheduleLoop(ctx.currentTime + 0.5)
    } catch {
      // Audio not supported
    }
  }, [getCtx, scheduleLoop])

  const stopMusic = useCallback(() => {
    if (loopTimerRef.current) clearTimeout(loopTimerRef.current)
    setMusicStarted(false)
    musicStartedRef.current = false
  }, [])

  const toggleMute = useCallback(() => {
    setIsMuted(m => {
      const next = !m
      isMutedRef.current = next
      if (masterRef.current && ctxRef.current) {
        masterRef.current.gain.setTargetAtTime(next ? 0 : 1, ctxRef.current.currentTime, 0.1)
      }
      return next
    })
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (loopTimerRef.current) clearTimeout(loopTimerRef.current)
      if (ctxRef.current) {
        ctxRef.current.close().catch(() => {})
      }
    }
  }, [])

  return { playSound, startMusic, stopMusic, toggleMute, isMuted, musicStarted }
}
